import { MontoColones } from '@/components/dominio/MontoColones';
import { MontoDolares } from '@/components/dominio/MontoDolares';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useResumenSemana, useSemanas } from '@/hooks/useFlujo';
import type { GrupoPagos, PagoConDetalle, SubgrupoPagos } from '@adelante/shared';
import { formatearColones, formatearDolares, formatearRangoSemana } from '@adelante/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

function FilaResumen({
  label,
  valor,
  negrita = false,
  separador = false,
}: {
  label: string;
  valor: string;
  negrita?: boolean;
  separador?: boolean;
}) {
  return (
    <div
      className={`flex justify-between items-center py-1 ${separador ? 'border-t border-border mt-1 pt-2' : ''}`}
    >
      <span className={`text-sm ${negrita ? 'font-semibold' : 'text-muted-foreground'}`}>
        {label}
      </span>
      <span className={`text-sm tabular-nums ${negrita ? 'font-semibold' : ''}`}>{valor}</span>
    </div>
  );
}

// Mostrar si hay cualquier dato: adeudado, monto, o confirmación
const tienePago = (p: PagoConDetalle) =>
  p.montoAdeudado != null || p.montoProgramado != null || p.montoPagado != null || p.pagado;

// ─── Fila de pago solo lectura ────────────────────────────────────────────────

function FilaPagoHistorial({ pago }: { pago: PagoConDetalle }) {
  const monedaEfectiva = pago.monedaPago ?? pago.concepto.moneda;
  const MontoComp = monedaEfectiva === 'C' ? MontoColones : MontoDolares;

  return (
    <tr className={`border-b last:border-0 ${pago.pagado ? 'bg-green-50/30' : ''}`}>
      <td className="py-1.5 pl-1 pr-3 w-[38%]">
        <span className={`text-sm ${pago.pagado ? 'text-muted-foreground' : ''}`}>
          {pago.concepto.nombre}
        </span>
      </td>
      <td className="py-1.5 px-3 text-right w-[19%] border-l border-border/40 tabular-nums text-sm text-muted-foreground">
        <MontoComp monto={pago.montoAdeudado} />
      </td>
      <td className="py-1.5 px-3 text-right w-[19%] border-l border-border/40 tabular-nums text-sm">
        <MontoComp monto={pago.montoProgramado} />
      </td>
      <td className="py-1.5 px-3 text-right w-[19%] border-l border-border/40 tabular-nums text-sm font-medium">
        <MontoComp monto={pago.montoPagado} />
      </td>
      <td className="py-1.5 pl-3 pr-1 text-center w-[5%] border-l border-border/40">
        {pago.pagado ? (
          <span className="text-green-600 text-xs font-bold">✓</span>
        ) : (
          <span className="text-muted-foreground/40 text-xs">—</span>
        )}
      </td>
    </tr>
  );
}

function TablaSubgrupoHistorial({ subgrupo }: { subgrupo: SubgrupoPagos }) {
  const conPago = subgrupo.pagos.filter(tienePago);
  if (conPago.length === 0) return null;
  return (
    <div className="ml-4 mt-1">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
        {subgrupo.categoria.nombre}
      </div>
      <table className="w-full">
        <tbody>
          {conPago.map((p) => (
            <FilaPagoHistorial key={p.id} pago={p} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GrupoHistorial({ grupo }: { grupo: GrupoPagos }) {
  const todosConPago = [
    ...grupo.pagos.filter(tienePago),
    ...grupo.subgrupos.flatMap((sg) => sg.pagos.filter(tienePago)),
  ];
  if (todosConPago.length === 0) return null;

  const pagados = todosConPago.filter((p) => p.pagado).length;

  return (
    <AccordionItem value={String(grupo.categoria.id)}>
      <AccordionTrigger className="hover:no-underline">
        <div className="flex items-center gap-3 flex-1 text-left">
          <span className="font-semibold text-sm">{grupo.categoria.nombre}</span>
          <span className="text-xs text-muted-foreground">
            {pagados}/{todosConPago.length}
          </span>
          <div className="ml-auto flex items-center gap-3 pr-2 text-xs tabular-nums text-muted-foreground">
            {grupo.subtotalProgramadoColones > 0 && (
              <span>{formatearColones(grupo.subtotalProgramadoColones)}</span>
            )}
            {grupo.subtotalProgramadoDolares > 0 && (
              <span>{formatearDolares(grupo.subtotalProgramadoDolares)}</span>
            )}
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        {grupo.pagos.filter(tienePago).length > 0 && (
          <table className="w-full">
            <thead>
              <tr className="text-xs text-muted-foreground border-b">
                <th className="text-left pb-2 font-medium w-[38%]">Concepto</th>
                <th className="text-right pb-2 font-medium w-[19%] border-l border-border/40 pl-3">
                  Adeudado
                </th>
                <th className="text-right pb-2 font-medium w-[19%] border-l border-border/40 pl-3">
                  A pagar
                </th>
                <th className="text-right pb-2 font-medium w-[19%] border-l border-border/40 pl-3">
                  Pagado
                </th>
                <th className="text-center pb-2 font-medium w-[5%] border-l border-border/40 pl-3">
                  ✓
                </th>
              </tr>
            </thead>
            <tbody>
              {grupo.pagos.filter(tienePago).map((p) => (
                <FilaPagoHistorial key={p.id} pago={p} />
              ))}
            </tbody>
          </table>
        )}
        {grupo.subgrupos.map((sg) => (
          <TablaSubgrupoHistorial key={sg.categoria.id} subgrupo={sg} />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}

// ─── Pantalla principal ───────────────────────────────────────────────────────

export function HistorialPantalla() {
  const { data: semanas, isLoading } = useSemanas();
  const [semanaId, setSemanaId] = useState<number | null>(null);
  const [acordeonAbierto, setAcordeonAbierto] = useState<string[]>([]);

  // Índice actual en el array (ordenado desc: 0 = más reciente)
  const idSeleccionado = semanaId ?? semanas?.[0]?.id ?? null;
  const indiceActual = useMemo(
    () => semanas?.findIndex((s) => s.id === idSeleccionado) ?? -1,
    [semanas, idSeleccionado],
  );

  const irA = (delta: number) => {
    if (!semanas) return;
    const nuevo = semanas[indiceActual + delta];
    if (nuevo) setSemanaId(nuevo.id);
  };

  const { data: resumen, isLoading: cargandoResumen } = useResumenSemana(idSeleccionado);

  // biome-ignore lint/correctness/useExhaustiveDependencies: solo re-expandir cuando cambia de semana, no en cada refetch de resumen
  useEffect(() => {
    if (resumen) setAcordeonAbierto(resumen.grupos.map((g) => String(g.categoria.id)));
  }, [resumen?.semana.id]);

  if (isLoading) return <div className="text-muted-foreground text-sm">Cargando historial...</div>;
  if (!semanas?.length) return <div className="text-muted-foreground text-sm">Sin datos.</div>;

  const semanaActual = semanas.find((s) => s.id === idSeleccionado);
  const porcentaje = resumen
    ? Math.round((resumen.pagosCompletados / Math.max(resumen.pagosTotal, 1)) * 100)
    : 0;

  return (
    <div className="space-y-5">
      {/* Header + navegación */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1">
          <h1 className="text-xl font-bold">Historial</h1>
          {semanaActual && (
            <p className="text-sm text-muted-foreground">
              Semana {semanaActual.numeroSemana} ·{' '}
              {formatearRangoSemana(semanaActual.fechaInicio, semanaActual.fechaFin)}
            </p>
          )}
        </div>

        {/* Navegación con flechas + selector */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => irA(1)}
            disabled={indiceActual >= semanas.length - 1}
            title="Semana anterior (más antigua)"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Select value={String(idSeleccionado)} onValueChange={(v) => setSemanaId(Number(v))}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Seleccionar semana" />
            </SelectTrigger>
            <SelectContent>
              {semanas.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  Sem {s.numeroSemana} / {s.anio} —{' '}
                  {formatearRangoSemana(s.fechaInicio, s.fechaFin)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => irA(-1)}
            disabled={indiceActual <= 0}
            title="Semana siguiente (más reciente)"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {cargandoResumen && <div className="text-muted-foreground text-sm">Cargando...</div>}

      {resumen && (
        <>
          {/* Panel de cajas — igual que Semana Actual pero solo lectura */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4 space-y-0.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block mb-2">
                Disponible
              </span>
              <FilaResumen label="Quinta" valor={formatearColones(resumen.semana.quinta ?? 0)} />
              <FilaResumen
                label="Bancos Colones"
                valor={formatearColones(resumen.semana.bancoColones ?? 0)}
              />
              <FilaResumen
                label="Bancos Dólares"
                valor={formatearDolares(resumen.semana.bancoDolares ?? 0)}
              />
              <FilaResumen
                label="Tipo de Cambio"
                valor={
                  resumen.semana.tipoDeCambio
                    ? `₡${resumen.semana.tipoDeCambio.toLocaleString('es-CR')}`
                    : '—'
                }
              />
              <FilaResumen
                label="Total Colonizado"
                valor={formatearColones(resumen.totalColonizado)}
                negrita
                separador
              />
            </div>
            <div className="border rounded-lg p-4 space-y-0.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block mb-2">
                Resumen de Pagos
              </span>
              <FilaResumen
                label="Total a Pagar ₡"
                valor={formatearColones(resumen.totalProgramadoColones)}
              />
              <FilaResumen
                label="Total a Pagar $"
                valor={formatearDolares(resumen.totalProgramadoDolares)}
              />
              <FilaResumen
                label="Total Pagos Colonizado"
                valor={formatearColones(resumen.totalPagosColonizado)}
                negrita
                separador
              />
              <FilaResumen
                label="Disponible Bancos"
                valor={formatearColones(resumen.disponibleBancos)}
                separador
              />
              <FilaResumen label="− Pagos" valor={formatearColones(resumen.totalPagosColonizado)} />
              <div
                className={`flex justify-between items-center py-1.5 mt-1 border-t-2 ${resumen.totalFinalCajas >= 0 ? 'border-green-400' : 'border-red-400'}`}
              >
                <span className="text-sm font-bold">TOTAL FINAL CAJAS</span>
                <span
                  className={`text-sm font-bold tabular-nums ${resumen.totalFinalCajas >= 0 ? 'text-green-700' : 'text-red-600'}`}
                >
                  {formatearColones(resumen.totalFinalCajas)}
                </span>
              </div>
            </div>
          </div>

          {/* Resumen financiero */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tarjeta label="A Pagar ₡" valor={formatearColones(resumen.totalProgramadoColones)} />
            <Tarjeta label="A Pagar $" valor={formatearDolares(resumen.totalProgramadoDolares)} />
            <Tarjeta label="Pagado ₡" valor={formatearColones(resumen.totalPagadoColones)} acento />
            <Tarjeta label="Pagado $" valor={formatearDolares(resumen.totalPagadoDolares)} acento />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tarjeta
              label="Total Pagos Colonizado"
              valor={formatearColones(resumen.totalPagosColonizado)}
            />
            <Tarjeta
              label="Total Final Cajas"
              valor={formatearColones(resumen.totalFinalCajas)}
              color={resumen.totalFinalCajas >= 0 ? 'verde' : 'rojo'}
            />
            <Tarjeta
              label="Tipo de Cambio"
              valor={
                resumen.semana.tipoDeCambio
                  ? `₡${resumen.semana.tipoDeCambio.toLocaleString('es-CR')}`
                  : '—'
              }
            />
            <Tarjeta
              label="Pagos confirmados"
              valor={`${resumen.pagosCompletados} / ${resumen.pagosTotal}`}
            />
          </div>

          {/* Progreso */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Progreso</span>
              <span>{porcentaje}%</span>
            </div>
            <Progress value={porcentaje} />
          </div>

          {/* Detalle por categoría */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={() => setAcordeonAbierto(resumen.grupos.map((g) => String(g.categoria.id)))}
            >
              Expandir todo
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={() => setAcordeonAbierto([])}
            >
              Contraer todo
            </Button>
          </div>
          <Accordion type="multiple" value={acordeonAbierto} onValueChange={setAcordeonAbierto}>
            {resumen.grupos.map((grupo) => (
              <GrupoHistorial key={grupo.categoria.id} grupo={grupo} />
            ))}
          </Accordion>
        </>
      )}
    </div>
  );
}

function Tarjeta({
  label,
  valor,
  acento = false,
  color,
}: {
  label: string;
  valor: string;
  acento?: boolean;
  color?: 'verde' | 'rojo';
}) {
  const bg =
    color === 'verde'
      ? 'bg-green-50 border-green-200'
      : color === 'rojo'
        ? 'bg-red-50 border-red-200'
        : acento
          ? 'bg-primary/5 border-primary/20'
          : '';
  const txt = color === 'verde' ? 'text-green-700' : color === 'rojo' ? 'text-red-700' : '';
  return (
    <div className={`border rounded-lg p-3 ${bg}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-sm font-semibold tabular-nums mt-0.5 ${txt}`}>{valor}</p>
    </div>
  );
}
